import axios from 'axios';

/**
 * API 기본 URL을 반환한다
 * 환경변수 REACT_APP_API_URL을 사용하여 환경별로 다른 URL을 반환
 * @returns {string} API URL
 */
export const getMainUrl = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:8081';
};

/**
 * 채팅 서버 URL을 반환한다
 * 환경변수 REACT_APP_CHAT_URL을 사용하여 환경별로 다른 URL을 반환
 * @returns {string} Chat URL
 */
export const getChatUrl = () => {
  return process.env.REACT_APP_CHAT_URL || 'http://localhost:8083';
};

/**
 * 토큰 없이 API를 호출한다
 * @param {string} path - API 경로
 * @param {string} method - HTTP 메서드
 * @param {Object} data - 요청 데이터
 * @returns {Promise} API 응답
 */
export async function mainCallerWithOutToken(path, method, data = null) {
  const options = {
    url: `${getMainUrl()}/${path}`,
    headers: {
      'Content-type': 'application/json',
      Accept: 'application/json',
    },
    method,
    withCredentials: false,
  };
  if (data) {
    options.data = data;
  }

  const res = await axios(options).catch(err => {
    console.log(err);
    throw err;
  });
  return res.data;
}

/**
 * 로컬스토리지에서 인증 토큰을 가져온다
 * @returns {string} 인증 토큰
 * @throws {Error} 토큰이 없을 경우 에러
 */
export const getToken = () => {
  const string = localStorage.getItem('pila-user');
  if (string) {
    const json = JSON.parse(string);
    return json.user.token;
  }
  // 토큰이 없으면 로그인 페이지로 리다이렉트
  window.alert('로그인 세션이 만료되었습니다.\n다시 로그인 해주세요.');
  localStorage.removeItem('pila-user');
  localStorage.removeItem('user-menu');
  window.location.replace('/login');
  return null;
};

/**
 * 지금 화면에서 보고 있는 지점 ID.
 * 토큰의 branchId 는 "로그인한 지점"이라 헤더에서 지점을 바꿔도 그대로다.
 * 서버가 "어느 지점에서 처리했는지"를 기록할 때 쓰라고 매 요청에 실어 보낸다.
 */
export const getCurrentBranchId = () => {
  try {
    const string = localStorage.getItem('pila-user');
    if (!string) return null;
    return JSON.parse(string)?.user?.branch?.branchId ?? null;
  } catch {
    return null;
  }
};

/**
 * 인증 토큰과 함께 API를 호출한다
 * @param {string} path - API 경로
 * @param {string} method - HTTP 메서드 (기본값: GET)
 * @param {Object} data - 요청 데이터
 * @param {Object} headers - 추가 헤더
 * @param {string} type - API 타입 ('MAIN' | 'CHAT')
 * @returns {Promise} API 응답
 */
async function mainCaller(path, method = 'GET', data = null, headers = {}, type = 'MAIN') {
  if (!headers.Authorization) {
    headers.Authorization = 'Bearer ' + getToken();
  }
  // 처리 지점 추적용. 값이 없으면 서버가 토큰의 로그인 지점으로 되돌아간다.
  const currentBranchId = getCurrentBranchId();
  if (currentBranchId != null && !headers['X-Branch-Id']) {
    headers['X-Branch-Id'] = String(currentBranchId);
  }
  const options = {
    method,
    url: type === 'CHAT' ? `${getChatUrl()}/${path}` : `${getMainUrl()}/${path}`,
    headers: {
      'Content-type': 'application/json',
      Accept: 'application/json',
      ...headers,
    },
    withCredentials: false,
  };
  if (data) {
    options.data = data;
  }

  const res = await axios(options).catch(err => {
    console.log(err);
    throw err;
  });
  return res.data;
}

/**
 * 사용자 로그인을 수행한다
 * @param {string} password - 비밀번호
 * @param {string} username - 사용자 이름
 * @returns {Promise} 로그인 응답 (토큰 포함)
 */
export async function pilaAuthUser(password, username) {
  const { data } = await axios.post(
    getMainUrl() + '/admin/api/v1/auth/login',
    {
      username: username,
      password: password,
    },
    {
      headers: {
        'content-type': 'application/json',
      },
    },
  );

  return data;
}

export default mainCaller;
